import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

// Note: Ensure Font Awesome CDN is in index.html:
// <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">

const QuizComponent = ({ assessment, questions, onBack }) => {
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(assessment.duration * 60);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [questionStatus, setQuestionStatus] = useState(
    Array(questions.length).fill('pending') // 'pending', 'correct', 'wrong', 'skipped'
  );

  useEffect(() => {
    if (timeLeft <= 0) {
      setQuizCompleted(true);
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleAnswerChange = (questionId, optionIndex) => {
    setAnswers((prev) => {
      const currentAnswers = prev[questionId] || [];
      const question = questions.find((q) => q._id === questionId);
      if (!question) return prev;

      if (question.type === 'single') {
        return { ...prev, [questionId]: [optionIndex] };
      } else {
        if (currentAnswers.includes(optionIndex)) {
          return {
            ...prev,
            [questionId]: currentAnswers.filter((idx) => idx !== optionIndex),
          };
        } else {
          return { ...prev, [questionId]: [...currentAnswers, optionIndex] };
        }
      }
    });

    // Reset status to pending if user changes answer
    if (questionStatus[currentQuestionIndex] === 'wrong') {
      setQuestionStatus((prev) => {
        const newStatus = [...prev];
        newStatus[currentQuestionIndex] = 'pending';
        return newStatus;
      });
    }
  };

  const handleSkip = () => {
    setQuestionStatus((prev) => {
      const newStatus = [...prev];
      newStatus[currentQuestionIndex] = 'skipped';
      return newStatus;
    });
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setQuizCompleted(true);
    }
  };

  const handleCheckAnswer = () => {
    const currentQuestion = questions[currentQuestionIndex];
    const userAnswers = answers[currentQuestion._id] || [];
    const isCorrect =
      userAnswers.length === currentQuestion.correctAnswers.length &&
      userAnswers.every((ans) => currentQuestion.correctAnswers.includes(ans));

    if (isCorrect) {
      toast.success('Correct Answer!', {
        position: 'top-right',
        autoClose: 2000,
      });
      setQuestionStatus((prev) => {
        const newStatus = [...prev];
        newStatus[currentQuestionIndex] = 'correct';
        return newStatus;
      });
      // Move to next question or complete quiz
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        setQuizCompleted(true);
      }
    } else {
      toast.error('Wrong Answer!', {
        position: 'top-right',
        autoClose: 2000,
      });
      setQuestionStatus((prev) => {
        const newStatus = [...prev];
        newStatus[currentQuestionIndex] = 'wrong';
        return newStatus;
      });
      // Stay on the same question to allow retry
    }
  };

  const handleQuestionClick = (index) => {
    setCurrentQuestionIndex(index);
  };

  const calculateScore = () => {
    let score = 0;
    questions.forEach((question) => {
      const userAnswers = answers[question._id] || [];
      const isCorrect =
        userAnswers.length === question.correctAnswers.length &&
        userAnswers.every((ans) => question.correctAnswers.includes(ans));
      if (isCorrect) score += 1;
    });
    return score;
  };

  const getSkippedCount = () => {
    return questionStatus.filter((status) => status === 'skipped').length;
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? `0${secs}` : secs}`;
  };

  const getSelectionHint = (correctAnswersLength) => {
    if (correctAnswersLength === 1) return '(Select One)';
    if (correctAnswersLength === 2) return '(Select Two)';
    return `(Select ${correctAnswersLength})`;
  };

  if (quizCompleted) {
    const score = calculateScore();
    const skipped = getSkippedCount();
    return (
      <div className="flex flex-col min-h-screen bg-gray-100">
        <div className="max-w-2xl mx-auto w-full py-12 px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-xl shadow-home-hero p-8">
            <h2 className="text-3xl font-raleway font-extrabold text-primary mb-4">
              Quiz Completed!
            </h2>
            <p className="text-lg font-montserrat text-secondary mb-2">
              Your score: {score} out of {questions.length}
            </p>
            <p className="text-lg font-montserrat text-secondary mb-8">
              Skipped: {skipped}
            </p>
            <button
              onClick={() => navigate('/assessmentDashboard')}
              className="admin-btn btn-primary px-8 py-3 rounded-full font-montserrat text-sm"
            >
              Back to Assessment
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const isCheckDisabled = !answers[currentQuestion._id] || answers[currentQuestion._id].length === 0;

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <div className="flex flex-1">
       

        {/* Main Content */}
        <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-raleway font-bold text-dark">
              {assessment.title}
            </h2>
            <div className="bg-primary text-white px-3 py-1 rounded-full text-sm font-montserrat">
              Time Left: {formatTime(timeLeft)}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-home-hero p-6 transition-all duration-300">
            <h3 className="text-xl font-raleway font-semibold text-primary mb-4">
              Question {currentQuestionIndex + 1} of {questions.length}
            </h3>
            <p className="text-secondary text-lg font-montserrat mb-6">
              {currentQuestion.questionText}{' '}
              <span className="text-sm text-gray-500">
                {getSelectionHint(currentQuestion.correctAnswers.length)}{' '}
                {/* (Correct Answers: {currentQuestion.correctAnswers.length}) */}
              </span>
            </p>
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => (
                <label
                  key={index}
                  className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition duration-150 cursor-pointer"
                >
                  <input
                    type={currentQuestion.type === 'single' ? 'radio' : 'checkbox'}
                    name={currentQuestion._id}
                    checked={(answers[currentQuestion._id] || []).includes(index)}
                    onChange={() => handleAnswerChange(currentQuestion._id, index)}
                    className="h-5 w-5 text-primary focus:ring-primary"
                  />
                  <span className="text-secondary text-base font-montserrat">{option}</span>
                </label>
              ))}
            </div>
            <div className="mt-8 flex justify-end space-x-3">
              <button
                onClick={handleSkip}
                className="admin-btn btn-outlined px-6 py-2 rounded-full font-montserrat text-sm"
              >
                Skip
              </button>
              <button
                onClick={handleCheckAnswer}
                disabled={isCheckDisabled}
                className={`admin-btn btn-primary px-6 py-2 rounded-full font-montserrat text-sm ${
                  isCheckDisabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Check Answer
              </button>
            </div>
          </div>
        </div>
         {/* Sidebar */}
        <div className="w-64 bg-white shadow-home-hero p-4">
          <h3 className="text-lg font-raleway font-bold text-primary mb-4">
            Progress
          </h3>
          <ul className="space-y-2">
            {questions.map((_, index) => (
              <li
                key={index}
                onClick={() => handleQuestionClick(index)}
                className={`flex items-center space-x-2 p-2 rounded-lg font-montserrat text-sm cursor-pointer ${
                  currentQuestionIndex === index
                    ? 'bg-primary text-white'
                    : questionStatus[index] === 'correct'
                    ? 'bg-green-100 text-green-500'
                    : questionStatus[index] === 'wrong'
                    ? 'bg-red-100 text-red-500'
                    : questionStatus[index] === 'skipped'
                    ? 'bg-gray-100 text-gray-500'
                    : 'bg-gray-100 text-secondary'
                }`}
              >
                <span>Question {index + 1}</span>
                {questionStatus[index] === 'correct' && (
                  <i className="fas fa-check text-green-500"></i>
                )}
                {questionStatus[index] === 'wrong' && (
                  <i className="fas fa-times text-red-500"></i>
                )}
                {questionStatus[index] === 'skipped' && (
                  <i className="fas fa-forward text-gray-500"></i>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-4">
            <p className="text-sm font-montserrat text-secondary">
              Correct: {calculateScore()}
            </p>
            <p className="text-sm font-montserrat text-secondary">
              Skipped: {getSkippedCount()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizComponent;